import { SidebarProvider } from "@/components/ui/sidebar";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AdminSidebar, AdminSidebarNav } from "@/components/admin/AdminSidebar";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Routes, Route, Navigate } from "react-router-dom";
import { CommandInbox } from "@/components/admin/CommandInbox";
import { Pipeline } from "@/components/admin/Pipeline";
import { ProspectsPage } from "@/components/admin/prospects/ProspectsPage";
import { Settings } from "@/components/admin/Settings";
import { BlogEditor } from "@/components/admin/BlogEditor";
import { ContentHub } from "@/components/admin/ContentHub";
import { LeadDetail } from "@/components/admin/LeadDetail";
import { ClientDrawerProvider } from "@/components/admin/ClientDrawer";
import { FinanceDashboard } from "@/components/admin/FinanceDashboard";
import { CommandPalette } from "@/components/admin/CommandPalette";
import { LinearOverview } from "@/components/admin/LinearOverview";
import { LinearIssues } from "@/components/admin/LinearIssues";
import { LinearProjectDetail } from "@/components/admin/LinearProjectDetail";
import { SalesDashboard } from "@/components/admin/SalesDashboard";
import { DeliveryOverview } from "@/components/admin/DeliveryOverview";
import { DeliveryProjectDetail } from "@/components/admin/DeliveryProjectDetail";

const Admin = () => {
  // The admin shell collapses to a mobile nav pattern (Sheet + hamburger)
  // below ~1000px — wider than the shadcn Sidebar primitive's own 768px
  // mobile breakpoint, since the new 248px-wide sidebar feels cramped as an
  // icon-collapse between 768–1000px. See use-mobile.tsx.
  const isMobile = useIsMobile(1000);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <SidebarProvider style={{ "--sidebar-width": "248px" } as React.CSSProperties}>
      <ClientDrawerProvider>
        <CommandPalette />
        <div className="flex min-h-screen w-full bg-background">
          {isMobile ? (
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetContent side="left" className="w-[248px] max-w-[85vw] p-0">
                <SheetHeader className="sr-only">
                  <SheetTitle>Menu do painel</SheetTitle>
                </SheetHeader>
                <AdminSidebarNav collapsed={false} onNavigate={() => setMobileNavOpen(false)} />
              </SheetContent>
            </Sheet>
          ) : (
            <AdminSidebar />
          )}
          <div className="flex min-w-0 flex-1 flex-col">
            <AdminHeader isMobile={isMobile} onOpenMobileNav={() => setMobileNavOpen(true)} />
            <main className="flex-1 p-6">
              <Routes>
                <Route path="/" element={<CommandInbox />} />
                <Route path="/pipeline" element={<Pipeline />} />
                <Route path="/leads-frios" element={<ProspectsPage />} />
                <Route path="/leads" element={<Navigate to="/admin/pipeline" replace />} />
                <Route path="/leads/:id" element={<LeadDetail />} />
                <Route path="/kanban" element={<Navigate to="/admin/pipeline" replace />} />
                <Route path="/issues" element={<Navigate to="/admin/linear/issues" replace />} />
                <Route path="/projetos" element={<DeliveryOverview />} />
                <Route path="/projetos/:id" element={<DeliveryProjectDetail />} />
                <Route path="/finance" element={<FinanceDashboard />} />
                <Route path="/conteudo" element={<ContentHub />} />
                {/* Legacy standalone manager routes — folded into the Conteúdo hub
                    (ContentHub.tsx) by Task 7b. Redirects keep old bookmarks/links
                    working. "/blog" is a leaf route (no children, no trailing "/*"),
                    so it only matches pathname exactly "/admin/blog" — it can never
                    swallow "/blog/new" or "/blog/edit/:id", which stay their own
                    routes and keep rendering BlogEditor as a full page. */}
                <Route
                  path="/testimonials"
                  element={<Navigate to="/admin/conteudo?tab=depoimentos" replace />}
                />
                <Route
                  path="/links"
                  element={<Navigate to="/admin/conteudo?tab=links" replace />}
                />
                <Route path="/blog" element={<Navigate to="/admin/conteudo?tab=blog" replace />} />
                <Route path="/blog/new" element={<BlogEditor />} />
                <Route path="/blog/edit/:id" element={<BlogEditor />} />
                <Route
                  path="/categories"
                  element={<Navigate to="/admin/conteudo?tab=categorias" replace />}
                />
                <Route
                  path="/projects"
                  element={<Navigate to="/admin/conteudo?tab=produtos" replace />}
                />
                <Route path="/budget" element={<Navigate to="/admin/pipeline" replace />} />
                <Route path="/sales" element={<SalesDashboard />} />
                <Route path="/linear" element={<LinearOverview />} />
                <Route path="/linear/issues" element={<LinearIssues />} />
                <Route path="/linear/projects/:id" element={<LinearProjectDetail />} />
                <Route
                  path="/analytics"
                  element={<Navigate to="/admin/finance?tab=analytics" replace />}
                />
                <Route path="/settings" element={<Settings />} />
                <Route
                  path="/api-docs"
                  element={<Navigate to="/admin/settings?tab=api" replace />}
                />
              </Routes>
            </main>
          </div>
        </div>
      </ClientDrawerProvider>
    </SidebarProvider>
  );
};

export default Admin;
